import React, { createContext, useContext, useCallback } from "react";
import { useAuth } from "./AuthContext";

interface PersistStateContextType {
  saveState: (key: string, state: any) => void;
  loadState: <T>(key: string) => T | null;
  clearState: (key: string) => void;
}

const PersistStateContext = createContext<PersistStateContextType | undefined>(undefined);

export function PersistStateProvider({ children }: { children: React.ReactNode }) {
  const { session } = useAuth();
  const unidadId = session?.unidad?.id || "";

  const saveState = useCallback(
    (key: string, state: any) => {
      if (!unidadId) return;
      try {
        const storageKey = `state_${unidadId}_${key}`;
        localStorage.setItem(storageKey, JSON.stringify(state));
      } catch (error) {
        console.error(`Error al guardar estado para ${key}:`, error);
      }
    },
    [unidadId]
  );

  const loadState = useCallback(<T,>(key: string): T | null => {
    if (!unidadId) return null;
    try {
      const storageKey = `state_${unidadId}_${key}`;
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error(`Error al cargar estado para ${key}:`, error);
      return null;
    }
  }, [unidadId]);

  const clearState = useCallback((key: string) => {
    if (!unidadId) return;
    try {
      const storageKey = `state_${unidadId}_${key}`;
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error(`Error al limpiar estado para ${key}:`, error);
    }
  }, [unidadId]);

  const value = { saveState, loadState, clearState };

  return (
    <PersistStateContext.Provider value={value}>
      {children}
    </PersistStateContext.Provider>
  );
}

export function usePersistState() {
  const context = useContext(PersistStateContext);
  if (!context) {
    throw new Error('usePersistState must be used within PersistStateProvider');
  }
  return context;
}